import { cn } from '@exam-room/utils';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment, useRef } from 'react';

import IconButton from '../icon-button';
import { CloseRoundedIcon } from '../icons';

export interface ModalProps extends React.ComponentPropsWithRef<'div'> {
  open: boolean;
  onClose?: () => void;
  children: React.ReactNode;
}

export default function Modal({ open, onClose, children, className, ...props }: ModalProps) {
  const closeButtonRef = useRef(null);

  return (
    <Transition show={open} as={Fragment}>
      <Dialog
        as='div'
        className='relative z-[9999]'
        onClose={onClose ?? (() => undefined)}
        initialFocus={closeButtonRef}
      >
        <Transition.Child
          as={Fragment}
          enter='ease-out duration-300'
          enterFrom='opacity-0'
          enterTo='opacity-60'
          leave='ease-in duration-200'
          leaveFrom='opacity-60'
          leaveTo='opacity-0'
        >
          <div className='fixed inset-0 bg-black opacity-60' />
        </Transition.Child>
        <div className='fixed inset-0 overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center p-4'>
            <Transition.Child
              as={Fragment}
              enter='ease-out duration-300'
              enterFrom='opacity-0 translate-y-4'
              enterTo='opacity-100 translate-y-0'
              leave='ease-in duration-200'
              leaveFrom='opacity-100 translate-y-0'
              leaveTo='opacity-0 translate-y-4'
            >
              <Dialog.Panel
                className={cn([
                  onClose ? 'lg:[&>*:first-child]:mr-6' : '',
                  'relative w-full transform overflow-hidden rounded-lg bg-white shadow-md transition-all',
                  className,
                ])}
                {...props}
              >
                {children}
                {onClose ? (
                  <IconButton
                    ref={closeButtonRef}
                    className='absolute top-4 right-4'
                    onClick={onClose}
                  >
                    <CloseRoundedIcon />
                  </IconButton>
                ) : null}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}
